import { useCallback, useEffect, useState } from "react";

import { create } from "@bufbuild/protobuf";
import { styled } from "@linaria/react";
import { useNavigate, useSearch } from "@tanstack/react-router";

import Spacing from "@gx/dls/containers/Spacing";
import HorizontalDivider from "@gx/dls/dividers/HorizontalDivider";
import { withTheme, type PropsWithTheme } from "@gx/dls/theme";
import {
  UpdateSourceRequestSchema,
  UpdateSourceResponse,
} from "@gx/ts-contracts/services/source/api/source_pb";
import { SourceResource } from "@gx/ts-contracts/services/source/resources/source_pb";

import EditSourceDrawerBody from "@/pages/sources/components/edit/EditSourceDrawerBody";
import EditSourceDrawerHeader from "@/pages/sources/components/edit/EditSourceDrawerHeader";
import EditSourceDrawerTabs from "@/pages/sources/components/edit/EditSourceDrawerTabs";
import EditSourceFormProvider from "@/pages/sources/components/edit/EditSourceFormProvider";
import { EditSourceDrawerTab, EditSourceFormState } from "@/pages/sources/components/edit/types";

const EditSourceDrawerWrapper = withTheme(styled.div<PropsWithTheme>`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  overflow: hidden;
  background-color: ${({ theme }) => theme.color.background.primary};
  border-radius: 6px;
`);

const EditSourceDrawerTabsWrapper = withTheme(styled.div<PropsWithTheme>`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 0 16px;
  border-bottom: 0.5px solid ${({ theme }) => theme.color.border.primary};
`);

interface EditSourceDrawerProps {
  source: SourceResource;
  onClose?: () => void;
  initialTab?: EditSourceDrawerTab;
}

const EditSourceDrawer = ({ source, onClose, initialTab }: EditSourceDrawerProps) => {
  const navigate = useNavigate();
  const { sourceTab } = useSearch({ from: "__root__" });

  const [activeTab, setActiveTab] = useState<EditSourceDrawerTab>(
    initialTab ?? EditSourceDrawerTab.OVERVIEW,
  );

  // Sync from URL when it changes (browser back/forward)
  useEffect(() => {
    if (sourceTab) {
      setActiveTab(sourceTab);
    }
  }, [sourceTab]);

  // Update URL when tab changes
  const handleTabChange = useCallback(
    (tab: EditSourceDrawerTab) => {
      setActiveTab(tab);
      void navigate({
        to: ".",
        search: (prev) => ({ ...prev, sourceTab: tab }),
      });
    },
    [navigate],
  );

  const initialState: EditSourceFormState = {
    source: source,
    isLoading: false,
    isUpdateSourceSucceeded: false,
    shouldShowErrors: false,
    errors: [],
    updateSourceRequest: create(UpdateSourceRequestSchema, {
      sourceId: source.id,
      name: source.name,
    }),
    updateSourceResponse: {} as UpdateSourceResponse,
  };

  return (
    <EditSourceFormProvider initialState={initialState}>
      <EditSourceDrawerWrapper>
        <EditSourceDrawerHeader onClose={onClose} />
        <Spacing bottom={8}>
          <HorizontalDivider />
        </Spacing>
        <EditSourceDrawerTabsWrapper>
          <EditSourceDrawerTabs activeTab={activeTab} onTabChange={handleTabChange} />
        </EditSourceDrawerTabsWrapper>
        <EditSourceDrawerBody activeTab={activeTab} onClose={onClose} />
      </EditSourceDrawerWrapper>
    </EditSourceFormProvider>
  );
};

export default EditSourceDrawer;
